import { User } from '../types';
import { supabase } from '../lib/clientPublic';

// Reset Password
const resetPassword = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${import.meta.env.VITE_SITE_URL}/reset-password`,
  });
  if (error) {
    const msg = error.message?.toLowerCase() || '';
    if (
      msg.includes('rate limit') ||
      msg.includes('too many requests') ||
      msg.includes('email rate limit exceeded') ||
      (error as any).status === 429
    ) {
      throw new Error(
        'Es wurden in kurzer Zeit zu viele E-Mails versendet. Bitte versuchen Sie es in wenigen Minuten erneut.'
      );
    }
    throw error;
  }
};

// LOGIN
const login = async (email: string, password: string): Promise<User> => {
  console.log('Attempting login for:', email);

  const {
    data: { session },
    error: signInError,
  } = await supabase.auth.signInWithPassword({ email, password });
  if (signInError) throw signInError;
  if (!session?.user) throw new Error('No session after login');

  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select(
      'id, email, name, company, course_progress, completed_modules, is_admin, is_super_admin, is_disabled, voucher_code, street, city, postal_code'
    )
    .eq('id', session.user.id)
    .single();

  if (profileError) throw profileError;
  if (!profile) throw new Error('No profile found');

  const isAdmin = session.user.app_metadata?.is_admin || false;
  const isSuperAdmin = session.user.app_metadata?.is_super_admin || false;
  const isDisabled = profile.is_disabled === true;
  const street = (profile as any).street || session.user.user_metadata?.street || '';
  const city = (profile as any).city || session.user.user_metadata?.city || '';
  const postalCode = (profile as any).postal_code || session.user.user_metadata?.postal_code || '';
  const voucher_code = (profile as any).voucher_code ?? null;
  const courseProgress = profile.course_progress || {
    modulesCompleted: {},
    allCompleted: false,
  };
  const completedModules = profile.completed_modules || [];

  const user: User = {
    id: session.user.id,
    email: email,
    name: profile.name || '',
    company: profile.company || '',
    courseProgress: courseProgress,
    completedModules: completedModules,
    isAdmin: isAdmin,
    isSuperAdmin: isSuperAdmin,
    isDisabled: isDisabled,
    street: street,
    city: city,
    postalCode: postalCode,
    voucher_code: voucher_code,
  };

  return user;
};

// LOGOUT
const logout = async (): Promise<void> => {
  localStorage.removeItem('currentUser');
  await supabase.auth.signOut({ scope: 'local' });
};

// getCurrentUser
const getCurrentUser = async (authUser?: any): Promise<User | null> => {
  console.log('In gcu');

  let sessionUser;
  if (authUser) {
    sessionUser = authUser;
    console.log('Using provided auth user data:', sessionUser.email);
  } else {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();
    console.log('session user:' + session?.user?.email);
    console.log('getCurrentUser: Session user metadata:', {
      email: session?.user?.email,
      disabled: session?.user?.user_metadata?.disabled,
      user_metadata: session?.user?.user_metadata
    });
    if (sessionError || !session?.user) return null;
    sessionUser = session.user;
  }

  const userId = sessionUser.id;

  let { data: profile, error: profileError } = await supabase
    .from('users')
    .select('id, email, name, company, course_progress, completed_modules, is_disabled, voucher_code, street, city, postal_code')
    .eq('id', userId)
    .maybeSingle();

  console.log('prof err: ' + profileError);
  if (profileError) return null;
  if (!profile) {
    const name = sessionUser.user_metadata?.name || '';
    const company = sessionUser.user_metadata?.company || '';
    const { data: newProfile, error: insertError } = await supabase
      .from('users')
      .insert({
        id: userId,
        email: sessionUser.email!,
        name: name,
        company: company,
        course_progress: { modulesCompleted: {}, allCompleted: false },
        completed_modules: [],
      })
      .select()
      .single();

    console.log('newProfile: ' + newProfile);
    if (insertError || !newProfile) return null;
    profile = newProfile;
  }

  const isUserAdmin = sessionUser.app_metadata?.is_admin === true;
  const isSuperAdminUser = sessionUser.app_metadata?.is_super_admin === true;
  const isUserDisabled = profile.is_disabled === true;

  console.log('getCurrentUser: Processing user flags:', {
    email: sessionUser.email,
    isUserAdmin,
    isSuperAdminUser,
    isUserDisabled,
    raw_is_disabled_value: profile.is_disabled
  });

  return {
    id: userId,
    email: sessionUser.email!,
    name: profile.name || '',
    company: profile.company || '',
    billingAddress: '',
    courseProgress: {
      modulesCompleted: profile.course_progress?.modulesCompleted || {},
      allCompleted: profile.course_progress?.allCompleted || false,
    },
    completedModules: profile.completed_modules || [],
    isAdmin: isUserAdmin,
    isSuperAdmin: isSuperAdminUser,
    isDisabled: isUserDisabled,
    street: (profile as any).street || sessionUser.user_metadata?.street || '',
    city: (profile as any).city || sessionUser.user_metadata?.city || '',
    postalCode: (profile as any).postal_code || sessionUser.user_metadata?.postal_code || '',
    voucher_code: (profile as any).voucher_code ?? null,
  };
};

// Update course progress
const updateCourseProgress = async (
  userId: string,
  moduleId: string,
  isCompleted: boolean
): Promise<User> => {
  const { data: currentUser, error: userError } = await supabase
    .from('users')
    .select('course_progress, completed_modules')
    .eq('id', userId)
    .single();

  if (userError) {
    console.error('Error fetching user data:', userError);
    throw new Error('Failed to fetch user data');
  }

  const courseProgress = currentUser.course_progress || {
    modulesCompleted: {},
    allCompleted: false,
  };
  courseProgress.modulesCompleted[moduleId] = isCompleted;

  const completedModules = Object.entries(courseProgress.modulesCompleted)
    .filter(([_, completed]) => completed)
    .map(([id]) => id);

  const allModules = [
    '1-intro',
    '2-basics',
    '3-chances',
    '4-dos',
    '5-examples',
    '6-guidelines',
    '7-summary',
  ];
  courseProgress.allCompleted = allModules.every(
    (id) => courseProgress.modulesCompleted[id]
  );

  const { data: updatedUser, error: updateError } = await supabase
    .from('users')
    .update({
      course_progress: courseProgress,
      completed_modules: completedModules,
    })
    .eq('id', userId)
    .select(
      'id, email, name, company, course_progress, completed_modules, is_admin, is_super_admin, is_disabled'
    )
    .single();

  if (updateError) {
    console.error('Error updating user progress:', updateError);
    throw new Error('Failed to update course progress');
  }

  if (!updatedUser) {
    throw new Error('No response received from server');
  }

  const isDisabled = updatedUser.is_disabled === true;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const street = session?.user?.user_metadata?.street || '';
  const city = session?.user?.user_metadata?.city || '';
  const postalCode = session?.user?.user_metadata?.postal_code || '';
  const voucher_code = session?.user?.user_metadata?.voucher_code || null;

  return {
    id: updatedUser.id,
    email: updatedUser.email,
    name: updatedUser.name || '',
    company: updatedUser.company || '',
    courseProgress: courseProgress,
    completedModules: completedModules,
    isAdmin: updatedUser.is_admin || false,
    isSuperAdmin: updatedUser.is_super_admin || false,
    isDisabled: isDisabled,
    street: street,
    city: city,
    postalCode: postalCode,
    voucher_code: voucher_code,
  };
};

// REGISTRIERUNG
const registerUser = async (userData: {
  name: string;
  email: string;
  company: string;
  street: string;
  city: string;
  postalCode: string;
  password: string;
  voucher?: string;
  agbAccepted: boolean;
}): Promise<void> => {
  if (
    !userData.email ||
    !userData.name ||
    !userData.company ||
    !userData.password
  ) {
    throw new Error('Bitte füllen Sie alle Pflichtfelder aus.');
  }

  if (userData.password.length < 8) {
    throw new Error('Das Passwort muss mindestens 8 Zeichen lang sein.');
  }
  if (!userData.agbAccepted) {
    throw new Error('Bitte akzeptieren Sie die AGB.');
  }

  if (userData.voucher && userData.voucher.trim()) {
    localStorage.setItem('activeVoucher', userData.voucher.trim());
    console.log('Voucher stored in localStorage');
  }
  localStorage.setItem('signup_email', userData.email);

  try {
    const {
      data: { session },
      error: signUpError,
    } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        emailRedirectTo: `${import.meta.env.VITE_SITE_URL}/auth/callback`,
        data: {
          name: userData.name,
          company: userData.company,
          voucher_code: userData.voucher?.trim() || null,
          street: userData.street,
          city: userData.city,
          postal_code: userData.postalCode,
          isAdmin: false,
          isSuperAdmin: false,
          disabled: true,
        },
      },
    });

    if (signUpError) {
      const msg = signUpError.message?.toLowerCase() || '';
      if (
        msg.includes('already registered') ||
        msg.includes('user already registered') ||
        msg.includes('email already in use') ||
        msg.includes('already exists') ||
        signUpError.status === 422
      ) {
        throw new Error(
          'Diese E-Mail-Adresse ist bereits registriert. Bitte nutzen Sie den Login oder wählen Sie eine andere E-Mail-Adresse.'
        );
      }
      if (
        msg.includes('rate limit') ||
        msg.includes('too many requests') ||
        msg.includes('email rate limit exceeded') ||
        signUpError.status === 429
      ) {
        throw new Error(
          'Es wurden zu viele Anfragen gesendet. Bitte warten Sie einige Minuten und versuchen Sie es erneut.'
        );
      }
      throw signUpError;
    }
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

// Update admin status via Edge Function
const updateAdminStatus = async (
  userId: string,
  newIsAdmin: boolean
): Promise<void> => {
  try {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error('Authentication session not found');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-user-roles`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId: userId,
          newIsAdmin: newIsAdmin,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update admin status');
    }

    const result = await response.json();
    console.log('Admin status update result:', result);
  } catch (error) {
    console.error('Error updating admin status:', error);
    throw error;
  }
};

// Create Enterprise User via Edge Function
const createEnterpriseUser = async (userData: {
  name: string;
  email: string;
  company: string;
}): Promise<{ user: User | null; error?: string }> => {
  try {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      throw new Error('Authentication session not found');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-enterprise-user`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name: userData.name,
          email: userData.email,
          company: userData.company,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return {
        user: null,
        error: errorData.error || 'Failed to invite enterprise user',
      };
    }

    const result = await response.json();
    console.log('Enterprise user invitation result:', result);

    return { user: null };
  } catch (error) {
    console.error('Error creating enterprise user:', error);
    return {
      user: null,
      error: error.message || 'Failed to invite enterprise user',
    };
  }
};

// New function to fetch user certificate
const getUserCertificate = async (userId: string) => {
  try {
    const { data: certificate, error } = await supabase
      .from('certificates')
      .select(
        `
        id,
        cert_number,
        issued_at,
        test_result_id,
        users (
          name,
          company
        )
      `
      )
      .eq('user_id', userId)
      .order('issued_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user certificate:', error);
      return null;
    }

    if (!certificate) {
      return null;
    }

    return {
      id: certificate.id,
      name: certificate.users.name,
      company: certificate.users.company,
      number: certificate.cert_number,
      date: new Date(certificate.issued_at).toLocaleDateString('de-DE'),
      testResultId: certificate.test_result_id,
    };
  } catch (error) {
    console.error('Unexpected error in getUserCertificate:', error);
    return null;
  }
};

export {
  login,
  logout,
  getCurrentUser,
  resetPassword,
  registerUser,
  updateCourseProgress,
  updateAdminStatus,
  createEnterpriseUser,
  getUserCertificate,
};
